import { useMemo } from 'react';
import {
  OUTCOMES,
  OUTCOME_COLORS,
  OUTCOME_LABELS,
  type CallFilterState,
  type LocationSummary,
  type SummaryResponse,
} from '../types';
import { formatCompact, formatDuration, formatNumber, formatPercent } from '../format';
import { StatTile } from '../components/StatTile';
import { StackedColumnChart } from '../components/StackedColumnChart';
import { HorizontalBarChart, type BarDatum } from '../components/HorizontalBarChart';

interface Props {
  summary: SummaryResponse | null;
  loading: boolean;
  locations: LocationSummary[];
  filters: CallFilterState;
  onFiltersChange: (next: CallFilterState) => void;
  onOpenCalls: () => void;
}

/** Stack order is the validated palette order — see styles.css. */
const CALL_SERIES = OUTCOMES.map((outcome) => ({
  key: outcome,
  label: OUTCOME_LABELS[outcome],
  color: OUTCOME_COLORS[outcome],
}));

function relativeChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / previous;
}

export function CallsOverview({ summary, loading, locations, filters, onFiltersChange, onOpenCalls }: Props) {
  const locationBars = useMemo<BarDatum[]>(() => {
    if (!summary) return [];
    return summary.byLocation.slice(0, 12).map((row) => ({
      key: row.locationId,
      label: row.locationName ?? row.locationId,
      value: row.total,
      tooltipRows: [
        { label: 'Calls', value: formatNumber(row.total) },
        { label: 'Answered', value: formatNumber(row.answered) },
        { label: 'No answer', value: formatNumber(row.no_answer) },
        { label: 'Talk time', value: formatDuration(row.talkSeconds) },
      ],
    }));
  }, [summary]);

  const userBars = useMemo<BarDatum[]>(() => {
    if (!summary) return [];
    const rows = summary.byUser.filter((row) => row.userId).slice(0, 12);
    // The same person can hold a seat in several sub-accounts, and two people
    // can share a name — qualify the label whenever it would otherwise repeat.
    const nameCounts = new Map<string, number>();
    for (const row of rows) {
      const name = row.userName ?? row.userId!;
      nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
    }
    return rows.map((row) => {
      const name = row.userName ?? row.userId!;
      const ambiguous = (nameCounts.get(name) ?? 0) > 1;
      return {
        key: `${row.userId}-${row.locationId}`,
        label: ambiguous && row.locationName ? `${name} · ${row.locationName}` : name,
        sublabel: row.locationName ?? undefined,
        value: row.total,
        tooltipRows: [
          { label: 'Calls', value: formatNumber(row.total) },
          { label: 'Answered', value: formatNumber(row.answered) },
          { label: 'No answer', value: formatNumber(row.no_answer) },
          { label: 'Talk time', value: formatDuration(row.talkSeconds) },
        ],
      };
    });
  }, [summary]);

  const automatedCalls = useMemo(
    () => summary?.byUser.filter((row) => !row.userId).reduce((sum, row) => sum + row.total, 0) ?? 0,
    [summary],
  );

  if (!summary) {
    return <p className="empty">{loading ? 'Loading report…' : 'No data yet — run a sync.'}</p>;
  }

  const { totals, previous } = summary;
  const callsChange = relativeChange(totals.calls, previous.calls);
  const answeredChange = relativeChange(totals.answered, previous.answered);

  return (
    <div className={loading ? 'stack reloading' : 'stack'}>
      <div className="grid grid-kpi">
        <StatTile
          label="Calls"
          value={formatCompact(totals.calls)}
          hero
          delta={callsChange === null ? null : { change: callsChange, higherIsBetter: true }}
        />
        <StatTile
          label="Answered"
          value={formatCompact(totals.answered)}
          delta={answeredChange === null ? null : { change: answeredChange, higherIsBetter: true }}
          footnote={`of ${formatNumber(totals.calls)} calls`}
        />
        <StatTile
          label="No answer"
          value={formatCompact(totals.noAnswer)}
          footnote={`${formatPercent(totals.calls ? totals.noAnswer / totals.calls : 0, 1)} of calls`}
        />
        <StatTile
          label="Voicemail"
          value={formatCompact(totals.voicemail)}
          footnote={`${formatNumber(totals.failed)} failed`}
        />
        <StatTile
          label="Total talk time"
          value={formatDuration(totals.talkSeconds)}
          footnote={`Avg ${formatDuration(totals.avgAnsweredDurationSeconds)} per answered call`}
        />
        <StatTile
          label="Outbound / inbound"
          compact
          value={`${formatCompact(totals.outbound)} / ${formatCompact(totals.inbound)}`}
          footnote={`${formatNumber(totals.uniqueContacts)} unique contacts`}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Calls per day by outcome</h2>
          <button type="button" className="btn btn-sm" onClick={onOpenCalls}>
            Open call log
          </button>
        </div>
        <p className="card-sub">
          Every call message HighLevel returned for the selected sub-accounts, bucketed by the day it
          was created in your local time zone.
        </p>
        <StackedColumnChart
          data={summary.timeseries}
          series={CALL_SERIES}
          ariaLabel="Calls per day by outcome"
        />
      </div>

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Calls by sub-account</h2>
            <span className="small muted">{summary.byLocation.length} with activity</span>
          </div>
          <p className="card-sub">Click a bar to filter the whole dashboard to that sub-account.</p>
          <HorizontalBarChart
            data={locationBars}
            onSelect={(locationId) => onFiltersChange({ ...filters, locationIds: [locationId], userIds: [] })}
          />
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Calls by user</h2>
            <span className="small muted">Top 12</span>
          </div>
          <p className="card-sub">
            {automatedCalls > 0
              ? `${formatNumber(automatedCalls)} more calls carried no user id — those are workflow, campaign or IVR calls.`
              : 'Every call in this range is attributed to a user.'}
          </p>
          <HorizontalBarChart data={userBars} labelGutter={210} />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Sub-account breakdown</h2>
          <span className="small muted">The table behind the charts</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Sub-account</th>
                <th className="num">Calls</th>
                <th className="num">Answered</th>
                <th className="num">No answer</th>
                <th className="num">Voicemail</th>
                <th className="num">Failed</th>
                <th className="num">Talk time</th>
                <th className="num">Callers</th>
              </tr>
            </thead>
            <tbody>
              {summary.byLocation.map((row) => {
                const location = locations.find((item) => item.id === row.locationId);
                return (
                  <tr key={row.locationId}>
                    <td className="nowrap">
                      <button
                        type="button"
                        className="btn btn-sm nowrap"
                        onClick={() =>
                          onFiltersChange({ ...filters, locationIds: [row.locationId], userIds: [] })
                        }
                      >
                        {row.locationName ?? location?.name ?? row.locationId}
                      </button>
                    </td>
                    <td className="num cell-strong">{formatNumber(row.total)}</td>
                    <td className="num">{formatNumber(row.answered)}</td>
                    <td className="num">{formatNumber(row.no_answer)}</td>
                    <td className="num">{formatNumber(row.voicemail)}</td>
                    <td className="num">{formatNumber(row.failed)}</td>
                    <td className="num">{formatDuration(row.talkSeconds)}</td>
                    <td className="num">{formatNumber(row.callers)}</td>
                  </tr>
                );
              })}
              {summary.byLocation.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty">
                    No calls in this range.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
